import React, { useState, useEffect, useMemo } from "react";
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Checkbox,
  Radio,
  Stack,
  LinearProgress,
  Typography,
  Grid,
  Alert,
  CircularProgress,
  IconButton,
  Tooltip,
  Box,
  Stepper,
  Step,
  StepLabel,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  List,
  ListItem,
  ListItemText,
  Divider,
} from "@mui/material";
import {
  Description,
  Settings,
  Map,
  CheckBox,
  CheckBoxOutlineBlank,
} from "@mui/icons-material";
import api, { base_URL } from "../../api";
import {
  getAkkDepthCode,
  getAkkTextureCode,
  getAkkSlopeCode,
  getLandUseCode,
  calculateAkkClass,
} from "../../utils/akkUtils";
import { calculateLegend } from "../../utils/legendUtils";
import { getMunsellRenkAdi } from "../../constants/munsellColorNames";

const steps = [
  "Konum ve Profil Seçimi",
  "Çevre Verileri Seçimi",
  "Çıktı Biçimi",
];

// Cevre bolumunde gosterilebilecek en fazla alan sayisi.
// Alanlar yatay sayfada 4 satira bolunerek yerlestirilir (5+4+4+4).
const MAX_ALAN = 17;
const SATIR_SAYISI = 4;

export default function JobMorphologyExportButton({ jobId, jobName }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [activeStep, setActiveStep] = useState(0);

  // Profil Seçimi State'leri
  const [profiles, setProfiles] = useState([]);
  const [fetchingProfiles, setFetchingProfiles] = useState(false);
  const [selectedProfileIds, setSelectedProfileIds] = useState([]);

  // Konum Filtreleri (İl -> İlçe -> Mahalle)
  const [filterIl, setFilterIl] = useState("ALL");
  const [filterIlce, setFilterIlce] = useState("ALL");
  const [filterMahalle, setFilterMahalle] = useState("ALL");

  // Belge olusturma ilerlemesi (yuzde + o an islenen profil)
  const [progress, setProgress] = useState(0);
  const [progressText, setProgressText] = useState("");

  // Resim tercihleri: belgeye horizon (profil kesiti) ve cevre fotograflari
  // eklensin mi? Kapatildiginda hem dosya kucuk kalir hem cok daha hizli uretilir.
  const [includeHorizonImage, setIncludeHorizonImage] = useState(true);
  const [includeEnvImages, setIncludeEnvImages] = useState(true);

  // Cikti bicimi: "word" -> duzenlenebilir .doc, "pdf" -> yazdirmaya hazir,
  // tum sayfalari yatay cevrilmis PDF (icerik dondurulmez).
  const [ciktiBicimi, setCiktiBicimi] = useState("word");

  // Çevre Verileri Seçimi
  const [selectedFields, setSelectedFields] = useState([
    "fizyografya",
    "egim_yuzde",
    "egim_yonu",
    "tesirli_derinlik",
    "btg",
    "toprak_serisi",
    "ana_materyal",
    "tabii_drenaj",
    "urun",
  ]);

  const API_URL = base_URL;

  useEffect(() => {
    if (open) {
      setActiveStep(0);
      setProgress(0);
      setProgressText("");
      setFilterIl("ALL");
      setFilterIlce("ALL");
      setFilterMahalle("ALL");
      fetchProfiles();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const fetchProfiles = async () => {
    setFetchingProfiles(true);
    try {
      const res = await api.get(`/admin/job-profiles/${jobId}`);
      if (res.data.success && res.data.data) {
        const parsedProfiles = res.data.data.map((p) => {
          let d = {};
          try {
            d =
              typeof p.full_data === "string"
                ? JSON.parse(p.full_data)
                : p.full_data;
          } catch (e) {}

          const splitLoc = p.ilce_mahalle ? p.ilce_mahalle.split("/") : [];

          return {
            ...p,
            parsedData: d,
            il:
              d?.il?.trim() ||
              (splitLoc[0] ? splitLoc[0].trim() : "Bilinmiyor"),
            ilce:
              d?.ilce?.trim() ||
              (splitLoc[1] ? splitLoc[1].trim() : "Bilinmiyor"),
            mahalle:
              d?.mahalle?.trim() ||
              (splitLoc[2] ? splitLoc[2].trim() : "Bilinmiyor"),
          };
        });

        setProfiles(parsedProfiles);
        setSelectedProfileIds([]);
      } else {
        setProfiles([]);
      }
    } catch (error) {
      console.error("Profiller çekilemedi:", error);
    } finally {
      setFetchingProfiles(false);
    }
  };

  const uniqueIls = useMemo(
    () => [...new Set(profiles.map((p) => p.il).filter(Boolean))].sort(),
    [profiles],
  );

  const uniqueIlces = useMemo(() => {
    let filtered = profiles;
    if (filterIl !== "ALL")
      filtered = filtered.filter((p) => p.il === filterIl);
    return [...new Set(filtered.map((p) => p.ilce).filter(Boolean))].sort();
  }, [profiles, filterIl]);

  const uniqueMahalles = useMemo(() => {
    let filtered = profiles;
    if (filterIl !== "ALL")
      filtered = filtered.filter((p) => p.il === filterIl);
    if (filterIlce !== "ALL")
      filtered = filtered.filter((p) => p.ilce === filterIlce);
    return [...new Set(filtered.map((p) => p.mahalle).filter(Boolean))].sort();
  }, [profiles, filterIl, filterIlce]);

  const filteredProfiles = useMemo(() => {
    // Liste de profil numarasina gore kucukten buyuge dizilir; metin
    // siralamasi P100'u P12'den once koydugu icin rakam degeri kullanilir.
    const numara = (p) =>
      parseInt(String(p.profil_no ?? "").replace(/\D/g, ""), 10) || 0;
    return profiles
      .filter((p) => {
        const matchIl = filterIl === "ALL" || p.il === filterIl;
        const matchIlce = filterIlce === "ALL" || p.ilce === filterIlce;
        const matchMahalle =
          filterMahalle === "ALL" || p.mahalle === filterMahalle;
        return matchIl && matchIlce && matchMahalle;
      })
      .sort((a, b) => numara(a) - numara(b));
  }, [profiles, filterIl, filterIlce, filterMahalle]);

  const handleIlChange = (e) => {
    setFilterIl(e.target.value);
    setFilterIlce("ALL");
    setFilterMahalle("ALL");
  };

  const handleIlceChange = (e) => {
    setFilterIlce(e.target.value);
    setFilterMahalle("ALL");
  };

  const handleMahalleChange = (e) => {
    setFilterMahalle(e.target.value);
  };

  const handleProfileToggle = (id) => {
    if (selectedProfileIds.includes(id)) {
      setSelectedProfileIds((prev) => prev.filter((pId) => pId !== id));
    } else {
      setSelectedProfileIds((prev) => [...prev, id]);
    }
  };

  const handleSelectAllFiltered = () => {
    const filteredIds = filteredProfiles.map((p) => p.id);
    const newSelected = new Set([...selectedProfileIds, ...filteredIds]);
    setSelectedProfileIds([...newSelected]);
  };

  const handleDeselectAllFiltered = () => {
    const filteredIds = filteredProfiles.map((p) => p.id);
    setSelectedProfileIds((prev) =>
      prev.filter((id) => !filteredIds.includes(id)),
    );
  };

  const availableOptions = [
    { key: "yeri", label: "Yeri" },
    // AKK sinifi artik sabit degil: isteyen bu alani secip tabloya ekler.
    { key: "akk_sinifi", label: "AKK Sınıfı" },
    // Formuller de sabit kutuda degil; istenirse alan olarak yerlestirilir.
    { key: "akk_formul", label: "AKK Formülü" },
    { key: "indeks_formul", label: "İndeks Formülü" },
    { key: "lejant", label: "Lejant (Harita Birimi)" },
    { key: "profilgrup", label: "Profil Grubu" },
    { key: "iklim", label: "İklim" },
    { key: "ort_sicaklik", label: "Ortalama Sıcaklık(°C)" },
    { key: "ort_yagis", label: "Ortalama Yağış" },
    { key: "fizyografya", label: "Fizyografya" },
    { key: "ana_materyal", label: "Ana Materyal" },
    { key: "kaya_tipi", label: "Kaya Tipi" },
    { key: "btg", label: "BTG" },
    { key: "mikrorolyef", label: "Mikrorölyef" },
    { key: "arazi_kullanim_sinif", label: "Arazi Kullanım Sınıfı" },
    { key: "arazi_kullanim_detay", label: "Arazi Kullanım Detayı" },
    { key: "urun", label: "Mevcut Ürün" },
    { key: "tesirli_derinlik", label: "Tesirli Derinlik(cm)" },
    { key: "kok_gel_derinlik", label: "Kök Gelişim Derinliği(cm)" },
    { key: "kok_gel_eng_faktor", label: "Kök Engel Faktörü" },
    { key: "egim_yuzde", label: "Eğim (%)" },
    { key: "egim_yonu", label: "Eğim Yönü (Bakı)" },
    { key: "tabii_drenaj", label: "Tabii Drenaj" },
    { key: "taban_suyu_min", label: "Taban Suyu Min(cm)" },
    { key: "taban_suyu_max", label: "Taban Suyu Max(cm)" },
    { key: "taban_suyu_belirti", label: "Taban Suyu Belirtisi" },
    { key: "sel_basmasi", label: "Sel Basması" },
    { key: "taskin_olasiligi", label: "Taşkın Olasılığı" },
    { key: "erozyon_sekli", label: "Erozyon Şekli" },
    { key: "su_erozyon_derecesi", label: "Erozyon Derecesi" },
    { key: "insan_etkisi", label: "İnsan Etkisi" },
    { key: "taslilik", label: "Taşlılık" },
    { key: "cakillilik", label: "Çakıllılık" },
    { key: "kayalilik", label: "Kayalılık" },
    { key: "yuzey_catlak", label: "Yüzey Çatlağı" },
    { key: "tuzluluk", label: "Tuzluluk" },
    { key: "alkalilik", label: "Alkalilik" },
    { key: "toprak_serisi", label: "Toprak Serisi" },
    { key: "dusunceler", label: "Notlar/Düşünceler" },
  ];

  const handleFieldToggle = (key) => {
    if (selectedFields.includes(key))
      setSelectedFields(selectedFields.filter((k) => k !== key));
    else {
      if (selectedFields.length < MAX_ALAN)
        setSelectedFields([...selectedFields, key]);
      else alert(`En fazla ${MAX_ALAN} özellik seçebilirsiniz!`);
    }
  };

  const getIndksSlopeCode = (slopeStr) => {
    if (!slopeStr) return "A";
    const s = String(slopeStr).trim();
    const directMapping = {
      "0-2": "A",
      "2-4": "1B",
      "4-6": "2B",
      "6-8": "1C",
      "8-10": "2C",
      "10-12": "3C",
      "12-14": "1D",
      "14-16": "2D",
      "16-18": "3D",
      "18-20": "4D",
      "20-30": "E",
      "30-45": "F",
      "45+": "G",
    };
    if (directMapping[s]) return directMapping[s];
    const numMatch = s.match(/[\d.]+/);
    if (!numMatch) return "A";
    const num = parseFloat(numMatch[0]);
    if (num < 2) return "A";
    if (num >= 2 && num < 4) return "1B";
    if (num >= 4 && num < 6) return "2B";
    if (num >= 6 && num < 8) return "1C";
    if (num >= 8 && num < 10) return "2C";
    if (num >= 10 && num < 12) return "3C";
    if (num >= 12 && num < 14) return "1D";
    if (num >= 14 && num < 16) return "2D";
    if (num >= 16 && num < 18) return "3D";
    if (num >= 18 && num < 20) return "4D";
    if (num >= 20 && num < 30) return "E";
    if (num >= 30 && num < 45) return "F";
    return "G";
  };

  // Yan faktorler formulde KOD olarak yazilir (Ç2, T2, R1, F1 gibi).
  // Sel basmasi verisi "Sık (F2)" bicimindeyse parantez icindeki kod alinir.
  const yanFaktorKodu = (deger) => {
    if (!deger) return "";
    const metin = String(deger).trim();
    const acilis = metin.indexOf("(");
    const kapanis = metin.indexOf(")");
    if (acilis > -1 && kapanis > acilis)
      return metin.slice(acilis + 1, kapanis).trim();
    return metin.split("-")[0].trim();
  };

  const calculateFullAkkString = (formData) => {
    // finalClass / subClassString burada kullanilmiyor: AKK sinifi artik
    // formulun basina eklenmiyor, getAkkSinifi ile ayri gosteriliyor.
    const { formulaPermeability } = calculateAkkClass(formData);
    const depth = getAkkDepthCode(formData.tesirli_derinlik);
    const rawTopTex = getAkkTextureCode(
      formData.horizons?.[0]?.tekstur,
      formData.horizons?.[0]?.kirec,
    );
    const topTex = rawTopTex.replace(/[0-9]/g, "");
    let subTex = "";
    if (formData.horizons?.[1]?.tekstur) {
      const rawSubTex = getAkkTextureCode(
        formData.horizons[1].tekstur,
        formData.horizons[1].kirec,
      );
      const cleanedSubTex = rawSubTex.replace(/[0-9]/g, "");
      if (cleanedSubTex !== topTex) subTex = cleanedSubTex;
      else if (formulaPermeability !== 4) subTex = cleanedSubTex;
    } else {
      const isShallow = ["4", "5"].includes(depth);
      if (!isShallow && formulaPermeability !== 4) subTex = topTex;
    }
    // NOT: Gecirgenlik rakami AKK formulunde YER ALMAZ.
    // Referans belge "Ust Topragin Bunyesi birimi", "Alt Topragin
    // Gecirgenligi" ve "Ikinci Onemli Katin Gecirgenligi" alanlarini
    // "yalnizca toprak indeks calismasinda kullanilir" diye isaretliyor.
    // (formulaPermeability yine de alt bunye kararinda kullaniliyor.)
    let salt = "";
    if (formData.tuzluluk) {
      if (
        formData.tuzluluk.includes("Hafif") ||
        formData.tuzluluk.includes("2")
      )
        salt = "2";
      else if (
        formData.tuzluluk.includes("Orta") ||
        formData.tuzluluk.includes("3")
      )
        salt = "3";
      else if (
        formData.tuzluluk.includes("Çok") ||
        formData.tuzluluk.includes("6")
      )
        salt = "6";
    }
    let drain = "";
    if (formData.tabii_drenaj) {
      if (
        formData.tabii_drenaj.includes("Fena") ||
        formData.tabii_drenaj.includes("(F)")
      )
        drain = "F";
      else if (
        formData.tabii_drenaj.includes("Yetersiz") ||
        formData.tabii_drenaj.includes("(K)")
      )
        drain = "K";
      else if (
        formData.tabii_drenaj.includes("Orta") ||
        formData.tabii_drenaj.includes("(O)")
      )
        drain = "O";
    }
    // Bos alanlarin yerine hicbir sey konmaz: yoksa yoktur.
    // (Eskiden hepsi bossa "1M" yaziliyordu - o varsayilan kaldirildi.)
    const numerator = `${depth}${topTex}${subTex}${salt}${drain}`.trim();
    const slope = getAkkSlopeCode(formData.egim_yuzde);
    const erosion = formData.su_erozyon_derecesi
      ? formData.su_erozyon_derecesi.charAt(0)
      : "1";
    const denominator = [slope, erosion].filter(Boolean).join("-");
    const gravel = formData.cakillilik?.split("-")[0]?.trim() || "";
    const stones = formData.taslilik?.split("-")[0]?.trim() || "";
    const rocks = formData.kayalilik?.split("-")[0]?.trim() || "";
    const landUse = getLandUseCode(formData.arazi_kullanim_sinif);
    // Sel basmasi (F1) resmi formulde yan faktorler arasinda; eksikti.
    const flood = yanFaktorKodu(formData.sel_basmasi);
    const sideFactors = [gravel, stones, rocks, landUse, flood]
      .filter((val) => {
        if (!val) return false;
        const v = val.toLocaleUpperCase("tr-TR");
        return !(
          v.includes("YOK") ||
          v.includes("ÇAKSIZ") ||
          v.includes("TAŞSIZ") ||
          v.includes("KAYASIZ") ||
          v === "R0"
        );
      })
      .join(" ");
    // Formulun basinda AKK SINIFI yazilmaz. Referans belgede o konumda
    // yalnizca profil grubu (V) bulunur ve o da "yalnizca toprak indeks
    // calismasinda" gecerlidir. AKK'de o yer bostur.
    return (
      `[(${numerator}) / (${denominator})]` +
      (sideFactors ? ` ${sideFactors}` : "")
    );
  };

  // AKK sinifi (ornek: "VIIIews") artik formulden ayri, kendi satirinda
  // gosteriliyor; formulun basina eklenmiyor.
  const getAkkSinifi = (formData) => {
    const { finalClass, subClassString } = calculateAkkClass(formData);
    return `${finalClass}${subClassString}`;
  };

  // Munsell kodundan okunabilir renk metni uretir: "kahverengi (10 YR 5/3)".
  // Once renk ADI, sonra kod yazilir. Kartta karsiligi olmayan kodlarda ad
  // uretilmez, yalnizca kodun kendisi kalir.
  const munsellRenkMetni = (hue, kod) => {
    const hueMetin = hue ? String(hue).trim() : "";
    const kodMetin = kod ? String(kod).trim() : "";
    if (!hueMetin && !kodMetin) return "";

    // Kod bicimi belgenin eski halindeki gibi: "10 YR (5/3)".
    // Renk adi bunun onune eklenir: "kahverengi 10 YR (5/3)".
    const kodlu = kodMetin
      ? (hueMetin ? hueMetin + " " : "") + "(" + kodMetin + ")"
      : hueMetin;

    const ad = getMunsellRenkAdi(hue, kod);
    return ad ? ad + " " + kodlu : kodlu;
  };

  // Kirec siddetinin morfoloji metnindeki sozel karsiligi.
  const KIREC_KARSILIKLARI = {
    "hic yok": "az",
    "hiç yok": "az",
    zayıf: "az",
    görünebilir: "orta",
    görülebilir: "orta",
    kuvvetli: "çok",
  };

  const kucult = (deger) =>
    deger ? String(deger).trim().toLocaleLowerCase("tr-TR") : "";

  // Bir metnin yalnizca ilk harfini buyutur (Turkce i -> I sorunu icin tr-TR).
  const basHarfBuyut = (deger) => {
    const metin = deger ? String(deger).trim() : "";
    if (!metin) return "";
    return metin.charAt(0).toLocaleUpperCase("tr-TR") + metin.slice(1);
  };

  const kirecMetni = (deger) => {
    const anahtar = kucult(deger);
    if (!anahtar) return "";

    // Dogrudan eslesme (KIREC_OPTIONS: "Hiç Yok" / "Zayıf" / ...)
    if (KIREC_KARSILIKLARI[anahtar]) return KIREC_KARSILIKLARI[anahtar];

    // Sahadaki eski kayitlar "K3 (Az Kireçli)" gibi kodlu geliyor;
    // parantez icindeki ifadeden siddeti cikariyoruz.
    if (anahtar.includes("çok az") || anahtar.includes("cok az")) return "az";
    if (anahtar.includes("az")) return "az";
    if (anahtar.includes("orta")) return "orta";
    if (anahtar.includes("çok") || anahtar.includes("cok")) return "çok";
    if (anahtar.includes("kireçsiz") || anahtar.includes("kirecsiz"))
      return "az";

    return anahtar;
  };

  const formatHorizonDescription = (h) => {
    // --- 1. RENK CUMLESI: kuru ve nemli, once ad sonra kod ---
    const renkParcalari = [];
    const kuruMetin = munsellRenkMetni(h.renk_sinif_kuru, h.renk_kod_kuru);
    const nemliMetin = munsellRenkMetni(h.renk_sinif_yas, h.renk_kod_yas);

    // Etiketler kucuk harfle ve deger ile arasinda bosluk birakilarak yazilir:
    // "kahverengi 10 YR (5/3) kuru, koyu kahverengi 10 YR (3/3) nemli"
    if (kuruMetin) renkParcalari.push(kuruMetin + " kuru");
    if (nemliMetin) renkParcalari.push(nemliMetin + " nemli");

    // --- 2. NOKTALI VIRGULLE AYRILAN BOLUMLER ---
    // Hicbir bolum kendi sonuna ";" koymaz; ayirici birlestirme sirasinda
    // eklenir. Boylece son bolumden sonra bosta ";" kalmaz.
    const bolumler = [];

    if (h.tekstur) {
      const acilis = h.tekstur.indexOf("(");
      const kapanis = h.tekstur.indexOf(")");
      const texName =
        acilis > -1 && kapanis > acilis
          ? h.tekstur.slice(acilis + 1, kapanis)
          : h.tekstur;
      bolumler.push(kucult(texName));
    }

    if (h.struktur_tip && h.struktur_tip.length > 0) {
      const tip = Array.isArray(h.struktur_tip)
        ? kucult(h.struktur_tip.join(", "))
        : kucult(h.struktur_tip);
      const strukturMetni = [
        kucult(h.struktur_dayaniklilik),
        kucult(h.struktur_buyukluk),
        tip,
      ]
        .filter(Boolean)
        .join(" ");
      if (strukturMetni) bolumler.push(strukturMetni);
    }

    const kivamArr = [];
    if (h.kivam_kuru) kivamArr.push("kuru iken " + kucult(h.kivam_kuru));
    if (h.kivam_nemli) kivamArr.push("nemli iken " + kucult(h.kivam_nemli));
    if (h.kivam_yas_yapiskanlik)
      kivamArr.push("yaş iken " + kucult(h.kivam_yas_yapiskanlik));
    if (h.kivam_yas_plastiklik) kivamArr.push(kucult(h.kivam_yas_plastiklik));
    if (kivamArr.length > 0) bolumler.push(kivamArr.join(", "));

    const kirec = kirecMetni(h.kirec);
    if (kirec) bolumler.push(kirec + " kireçli");

    if (h.taslilik && !kucult(h.taslilik).includes("taşsız"))
      bolumler.push(kucult(h.taslilik));

    if (h.kok_sacak_miktar && h.kok_sacak_miktar !== "Yok")
      bolumler.push(
        [
          kucult(h.kok_sacak_miktar),
          kucult(h.kok_sacak_kalinlik),
          "saçak kökler",
        ]
          .filter(Boolean)
          .join(" "),
      );

    if (h.kok_kazik_miktar && h.kok_kazik_miktar !== "Yok")
      bolumler.push(
        [
          kucult(h.kok_kazik_miktar),
          kucult(h.kok_kazik_kalinlik),
          "kazık kökler",
        ]
          .filter(Boolean)
          .join(" "),
      );

    if (h.sinir_belirginlik || h.sinir_sekil)
      bolumler.push(
        [kucult(h.sinir_belirginlik), kucult(h.sinir_sekil), "sınır"]
          .filter(Boolean)
          .join(" "),
      );

    // Ozel gorunum artik aciklama metnine girmiyor; horizon tablosunda
    // kendi sutununda gosteriliyor (bkz. formatOzelGorunum).

    // Bolumler KUCUK harfle yazilir; ";" sonrasi bas harf buyutulmez.
    // Hucrede yalnizca en bastaki (renk) harf buyuk olur.
    const govde = bolumler.filter(Boolean).join("; ");

    const renkCumlesi = renkParcalari.length
      ? renkParcalari.join(", ") + "; "
      : "";

    // renkCumlesi zaten "; " ile bittigi icin araya ikinci bir bosluk
    // koymuyoruz; aksi halde ciktida cift bosluk olusuyordu.
    // Yalnizca hucrenin ILK harfi (rengin bas harfi) buyutulur; devami
    // ve ";" ile ayrilan tum bolumler kucuk harfle kalir.
    return basHarfBuyut((renkCumlesi + (govde ? govde + "." : "")).trim());
  };

  // Horizon tablosundaki "ÖZEL GÖRÜNÜM" sutununun icerigi.
  // Tum harfler kucuk, yalnizca bas harf buyuk.
  const formatOzelGorunum = (h) => {
    const ham = Array.isArray(h.ozel_gorunum)
      ? h.ozel_gorunum.filter(Boolean).join(", ")
      : h.ozel_gorunum;
    const metin = kucult(ham);
    return metin ? basHarfBuyut(metin) : "";
  };

  const getAbsoluteUrl = (img) => {
    if (!img) return null;
    if (img.startsWith("data:")) return img;
    if (img.startsWith("http")) return img;
    const base = API_URL.endsWith("/") ? API_URL.slice(0, -1) : API_URL;
    const path = img.startsWith("/") ? img : `/${img}`;
    return `${base}${path}`;
  };

  const urlToBase64 = async (url, maxWidth = 600) => {
    try {
      const response = await fetch(url);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const blob = await response.blob();

      // Silinmis bir dosya istendiginde sunucu HTML dondurebiliyor; bunu resim
      // sanip belgeye gomersek cikti bozuluyor. Tur kontrolu ile eleniyor.
      if (!blob.type || !blob.type.startsWith("image/")) {
        throw new Error(`Resim degil: ${blob.type || "bilinmiyor"}`);
      }

      return new Promise((resolve, reject) => {
        const img = new Image();
        const objUrl = URL.createObjectURL(blob);

        img.onload = () => {
          const canvas = document.createElement("canvas");
          let width = img.width;
          let height = img.height;

          if (width > maxWidth) {
            height = Math.round((height * maxWidth) / width);
            width = maxWidth;
          }

          canvas.width = width;
          canvas.height = height;

          const ctx = canvas.getContext("2d");
          ctx.drawImage(img, 0, 0, width, height);

          const dataUrl = canvas.toDataURL("image/jpeg", 0.7);

          URL.revokeObjectURL(objUrl);
          resolve(dataUrl);
        };

        img.onerror = () => {
          URL.revokeObjectURL(objUrl);
          resolve(null);
        };

        img.src = objUrl;
      });
    } catch (error) {
      console.warn("Resim çevrilemedi veya engellendi:", url);
      return null;
    }
  };

  // Yazdirmaya hazir ciktiyi gizli bir iframe icinde acip tarayicinin
  // yazdirma penceresini tetikler. Kullanici hedef olarak "PDF olarak kaydet"
  // secerek dosyayi alir. Sunucuya dev boyutlu base64 gonderilmez ve sunucuda
  // ek bir kutuphane (puppeteer/chromium) gerekmez.
  const yazdirmaPenceresiAc = (html) =>
    new Promise((resolve) => {
      const blob = new Blob([html], { type: "text/html;charset=utf-8" });
      const url = URL.createObjectURL(blob);

      const iframe = document.createElement("iframe");
      iframe.style.position = "fixed";
      iframe.style.right = "0";
      iframe.style.bottom = "0";
      iframe.style.width = "0";
      iframe.style.height = "0";
      iframe.style.border = "0";
      iframe.src = url;

      iframe.onload = () => {
        try {
          iframe.contentWindow.focus();
          iframe.contentWindow.print();
        } catch (e) {
          console.error("Yazdirma penceresi acilamadi:", e);
        }
        // Yazdirma penceresi kapanmadan iframe'i kaldirmiyoruz.
        setTimeout(() => {
          URL.revokeObjectURL(url);
          if (iframe.parentNode) iframe.parentNode.removeChild(iframe);
        }, 120000);
        resolve();
      };

      document.body.appendChild(iframe);
    });

  const handleCreateMorphology = async () => {
    if (selectedProfileIds.length === 0) {
      alert("Lütfen indirebilmek için en az 1 profil seçiniz!");
      return;
    }

    const pdfMi = ciktiBicimi === "pdf";

    setLoading(true);
    setProgress(0);
    setProgressText("Veriler hazırlanıyor...");
    try {
      // Profiller KENDI numaralarina gore kucukten buyuge siralanir.
      // Yeniden numaralandirma YAPILMAZ: P1, P2, P3 gibi uydurma bir sira
      // kodu verilirse kaydin gercek profil numarasi anlamsizlasir.
      // (P12 ile P100 dogru siralansin diye rakam degeri karsilastirilir.)
      const profilSayi = (p) =>
        parseInt(String(p.profil_no ?? "").replace(/\D/g, ""), 10) || 0;
      const profilesToExport = profiles
        .filter((p) => selectedProfileIds.includes(p.id))
        .sort((a, b) => profilSayi(a) - profilSayi(b));

      // Profil numarasi kayitta nasil duruyorsa oyle yazilir ("P31" ise "P31").
      const profilNoYazi = (p) => String(p.profil_no ?? "").trim() || "-";

      const htmlParts = [];

      // Word: 1. sayfa dikey, 2. sayfa yatay (mevcut duzen korunur).
      // PDF : TUM sayfalar yatay; icerik dondurulmez, yalnizca kagit cevrilir.
      const wordStil = `
            @page Section1 { size: 21cm 29.7cm; margin: 1.5cm; }
            div.Section1 { page: Section1; }
            @page Section2 { size: 29.7cm 21cm; margin: 1.5cm; mso-page-orientation: landscape; }
            div.Section2 { page: Section2; }`;
      // PDF: KAGIT DIKEY. 1. sayfa Word'dekiyle birebir ayni akar.
      // 2. sayfanin icerigi ise oldugu gibi kalir, yalnizca kagida 90 derece
      // yatik basilir (okurken sayfa cevrilir). Olculer sunucudaki PDF
      // kenar boslugu (15mm) ile uyumludur: 210-30=180mm, 297-30=267mm.
      const pdfStil = `
            @page { size: A4 portrait; margin: 15mm; }
            div.Section1 { page-break-after: always; }
            div.Section2 {
              position: relative;
              width: 180mm; height: 267mm;
              overflow: hidden;
              page-break-after: always;
              page-break-inside: avoid;
            }
            div.Section2:last-of-type { page-break-after: auto; }
            /* Dikey sayfaya oturtulan YATIK cerceve: 267 x 180 mm */
            .yatayCerceve {
              position: absolute; top: 0; left: 0;
              width: 267mm; height: 180mm;
              transform-origin: top left;
              transform: translateY(267mm) rotate(-90deg);
            }
            .yatayIc { transform-origin: top left; }
            img { max-width: 100%; }`;

      htmlParts.push(`
        <html xmlns:o='urn:schemas-microsoft-com:office:office' xmlns:w='urn:schemas-microsoft-com:office:word' xmlns='http://www.w3.org/TR/REC-html40'>
        <head>
          <meta charset="utf-8">
          <title>Toplu Profil Morfolojisi - ${jobName}</title>
          <style>
${pdfMi ? pdfStil : wordStil}
            * { font-family: 'Arial', sans-serif !important; }
            body, table, tbody, thead, tr, th, td, div, span, p, b, strong, i, em { font-family: 'Arial', sans-serif !important; color: #000; }
            body { font-size: 9pt; line-height: 1.2; }
            table { width: 100%; border-collapse: collapse; margin-bottom: 8px; table-layout: fixed; }
            th, td { padding: 4px; vertical-align: top; }
            .col-hor { width: 10%; } .col-dep { width: 10%; } .col-des { width: 58%; } .col-ozel { width: 22%; }
          </style>
        </head>
        <body>
      `);

      for (let index = 0; index < profilesToExport.length; index++) {
        const profile = profilesToExport[index];
        const d = profile.parsedData || {};

        // Ilerleme: profil basina 5-95 arasi ilerler, kalan yuzde ekranda gorunur.
        setProgress(5 + Math.floor((index / profilesToExport.length) * 90));
        setProgressText(
          `Profil ${profile.profil_no || index + 1} işleniyor (${index + 1}/${profilesToExport.length})`,
        );
        // Tarayicinin arayuzu tazeleyebilmesi icin donguye nefes aldiriyoruz.
        await new Promise((resolve) => setTimeout(resolve, 0));

        const calculatedAkkString = calculateFullAkkString(d);
        const leg = calculateLegend(d) || {
          leftCode: "?",
          topString: "?",
          bottomString: "?",
          rightCode: "",
        };
        const calculatedLegendString = `${leg.leftCode} [(${leg.topString}) / (${leg.bottomString})] ${leg.rightCode}`;

        const pGrup = d.profilgrup || "I";
        const depth = getAkkDepthCode(d.tesirli_derinlik);
        const rawTopTex = getAkkTextureCode(
          d.horizons?.[0]?.tekstur,
          d.horizons?.[0]?.kirec,
        );
        const topTex = rawTopTex.replace(/[0-9]/g, "");

        let subTex = "";
        const { formulaPermeability } = calculateAkkClass(d);
        if (d.horizons?.[1]?.tekstur) {
          const rawSubTex = getAkkTextureCode(
            d.horizons[1].tekstur,
            d.horizons[1].kirec,
          );
          const cleanedSubTex = rawSubTex.replace(/[0-9]/g, "");
          if (cleanedSubTex !== topTex) subTex = cleanedSubTex;
          else if (formulaPermeability !== 4) subTex = cleanedSubTex;
        } else {
          const isShallow = ["4", "5"].includes(depth);
          if (!isShallow && formulaPermeability !== 4) subTex = topTex;
        }

        let permStr =
          formulaPermeability && formulaPermeability !== 4
            ? formulaPermeability.toString()
            : "";
        let salt = "";
        if (d.tuzluluk) {
          if (d.tuzluluk.includes("Hafif") || d.tuzluluk.includes("2"))
            salt = "2";
          else if (d.tuzluluk.includes("Orta") || d.tuzluluk.includes("3"))
            salt = "3";
          else if (d.tuzluluk.includes("Çok") || d.tuzluluk.includes("6"))
            salt = "6";
        }
        let drain = "";
        if (d.tabii_drenaj) {
          if (d.tabii_drenaj.includes("Fena") || d.tabii_drenaj.includes("(F)"))
            drain = "F";
          else if (
            d.tabii_drenaj.includes("Yetersiz") ||
            d.tabii_drenaj.includes("(K)")
          )
            drain = "K";
          else if (
            d.tabii_drenaj.includes("Orta") ||
            d.tabii_drenaj.includes("(O)")
          )
            drain = "O";
        }

        const slopeClass = getIndksSlopeCode(d.egim_yuzde);
        const erosion = d.su_erozyon_derecesi
          ? d.su_erozyon_derecesi.charAt(0)
          : "1";
        const gravel = d.cakillilik?.split("-")[0]?.trim() || "";
        const stones = d.taslilik?.split("-")[0]?.trim() || "";
        const rocks = d.kayalilik?.split("-")[0]?.trim() || "";
        const landUse = getLandUseCode(d.arazi_kullanim_sinif);

        // Resmi formul sirasi (AKK referans belgesi):
        //   V [( 1 F2 M2 3 F 2 ) / ( 2 B-2 )] Ç T2 R1 K F1
        // Bor ve alkalilik resmi formulde YOK; ayrica veride de hic dolmuyordu,
        // bu yuzden formulde bos yuva birakiyorlardi. Cikarildilar.
        // Sel basmasi (F1) ise eksikti, yan faktorlere eklendi.
        const flood = yanFaktorKodu(d.sel_basmasi);

        const sideFactors = [gravel, stones, rocks, landUse, flood]
          .filter((val) => {
            if (!val) return false;
            const v = val.toLocaleUpperCase("tr-TR");
            return !(
              v.includes("YOK") ||
              v.includes("ÇAKSIZ") ||
              v.includes("TAŞSIZ") ||
              v.includes("KAYASIZ") ||
              v === "R0"
            );
          })
          .join(" ");

        // Bos degerler formulde bosluk birakmasin diye filtreleniyor.
        const idxPay = [depth, topTex, subTex, permStr, salt, drain]
          .filter(Boolean)
          .join(" ");
        const idxPayda = [slopeClass, erosion].filter(Boolean).join("-");

        const calculatedIndexFormula =
          `${pGrup} [(${idxPay}) / (${idxPayda})]` +
          (sideFactors ? ` ${sideFactors}` : "");

        const horizonRows =
          d.horizons
            ?.map(
              (h) => `
          <tr style="mso-height-rule:exactly;">
            <td style="text-align:center; font-weight:bold; border: 1px solid #000; padding: 4px; font-size: 8pt;">${h.sembol || "-"}</td>
            <td style="text-align:center; border: 1px solid #000; padding: 4px; font-size: 8pt;">${h.derinlik_min}-${h.derinlik_max}${h.derinlik_arti ? "+" : ""}</td>
            <td style="border: 1px solid #000; text-align: left; padding: 4px; font-size: 8.5pt;">${formatHorizonDescription(h)}</td>
            <td style="border: 1px solid #000; text-align: left; padding: 4px; font-size: 8.5pt;">${formatOzelGorunum(h)}</td>
          </tr>
        `,
            )
            .join("") ||
          "<tr><td colspan='4' style='font-size: 8.5pt; text-align:center;'>Veri yok</td></tr>";

        let horizonImgFinal = null;
        const absHorizonUrl = includeHorizonImage
          ? getAbsoluteUrl(d.resim_horizon)
          : null;
        if (absHorizonUrl) {
          horizonImgFinal = await urlToBase64(absHorizonUrl, 400);
        }

        const horizonImgHtmlModified = horizonImgFinal
          ? `<div style="width:250px; height:400px; overflow:hidden; border:1px solid #000; text-align:center;">
              <img src="${horizonImgFinal}" width="250" height="400" style="width:250px; height:400px; display:block;" />
            </div>`
          : includeHorizonImage
            ? `<div style="text-align:center; padding:15px; border:1px dashed #ccc; font-size: 8pt; width: 250px; height: 400px;">Horizon resmi yok</div>`
            : "";

        // 🔥 TÜM RESİMLERİ İŞLEME VE SATIRLARA BÖLME
        const envImagesFinal = [];
        if (
          includeEnvImages &&
          d.resimler_cevre &&
          d.resimler_cevre.length > 0
        ) {
          for (const img of d.resimler_cevre) {
            const absUrl = getAbsoluteUrl(img);
            if (absUrl) {
              const b64 = await urlToBase64(absUrl, 300);
              if (b64) envImagesFinal.push(b64);
            }
          }
        }

        let envImagesHtml = "";
        if (envImagesFinal.length > 0) {
          // Diziyi 4'erli parçalara bölen yardımcı fonksiyon
          const chunkArray = (arr, size) => {
            const result = [];
            for (let i = 0; i < arr.length; i += size) {
              result.push(arr.slice(i, i + size));
            }
            return result;
          };

          const chunks = chunkArray(envImagesFinal, 4);

          // Her 4'lü parça için bir HTML <tr> satırı oluşturuyoruz
          const rowsHtml = chunks
            .map((chunk) => {
              const tds = chunk
                .map(
                  (src) => `
              <td style="width:25%; padding:4px; text-align:center; border: none; vertical-align: top;">
                <div style="border:1px solid #999; padding: 2px; background: #fff;">
                  <img src="${src}" width="210" height="150" style="width:210px; height:150px; display:block;" />
                </div>
              </td>
            `,
                )
                .join("");

              // Satırda 4'ten az resim varsa boşlukları doldur (tablo kaymasın diye)
              const emptyCount = 4 - chunk.length;
              let emptyTds = "";
              for (let i = 0; i < emptyCount; i++) {
                emptyTds += `<td style="width:25%; border:none;"></td>`;
              }

              return `<tr style="mso-height-rule:exactly; height:150px;">${tds}${emptyTds}</tr>`;
            })
            .join("");

          envImagesHtml = `
            <table style="width: 100%; margin-top: 15px; border: 1px solid #000; border-collapse: collapse; background: #fbfbfb; table-layout: fixed;">
              <tr>
                <td colspan="4" style="text-align:center; font-weight:bold; font-size:9pt; padding:4px; border-bottom:1px solid #000; background-color:#e0e0e0;">
                  ÇEVRE FOTOĞRAFLARI
                </td>
              </tr>
              ${rowsHtml}
            </table>
          `;
        }

        // Veride tutulmayan, hesaplanan alanlarin karsiliklari.
        const HESAPLANAN_ALANLAR = {
          akk_sinifi: () => getAkkSinifi(d),
          akk_formul: () => calculatedAkkString,
          indeks_formul: () => calculatedIndexFormula,
          lejant: () => calculatedLegendString,
        };

        const selectedData = selectedFields.map((key) => {
          const option = availableOptions.find((opt) => opt.key === key);
          if (!option)
            return { label: key.toUpperCase(), value: d[key] || "-" };
          const hesapla = HESAPLANAN_ALANLAR[key];
          let val = hesapla
            ? hesapla() || "-"
            : option.valueOverride || d[key] || "-";
          if (key === "egim_yuzde" && val !== "-") val = `% ${val}`;
          if (key === "yukseklik" && val !== "-") val = `${val} m`;
          return { label: option.label, value: val };
        });

        // Alanlar 4 satira mumkun oldugunca esit dagitilir (17 alan -> 5+4+4+4).
        const alanSatirlari = [];
        if (selectedData.length > 0) {
          const temel = Math.floor(selectedData.length / SATIR_SAYISI);
          const fazla = selectedData.length % SATIR_SAYISI;
          let imlec = 0;
          for (let satir = 0; satir < SATIR_SAYISI; satir++) {
            const adet = temel + (satir < fazla ? 1 : 0);
            if (adet > 0)
              alanSatirlari.push(selectedData.slice(imlec, imlec + adet));
            imlec += adet;
          }
        }

        const generateTableHtml = (dataItems) => {
          if (dataItems.length === 0) return "";
          const headers = dataItems
            .map(
              (item) =>
                `<th style="border: 1px solid #000; background-color: #f0f0f0; font-weight: bold; text-align: center; font-size: 8pt; padding: 4px;">${item.label.toUpperCase()}</th>`,
            )
            .join("");
          const cells = dataItems
            .map(
              (item) =>
                `<td style="border: 1px solid #000; text-align: center; font-size: 8pt; padding: 4px;">${item.value}</td>`,
            )
            .join("");
          return `<table style="width: 100%; border-collapse: collapse; margin-bottom: 5px; table-layout: fixed;"><thead><tr>${headers}</tr></thead><tbody><tr>${cells}</tr></tbody></table>`;
        };

        let combinedNotes =
          d.dusunceler || d.arazi_aciklama || "Not bulunmamaktadır.";
        if (d.taban_suyu_belirti)
          combinedNotes += ` Taban suyu belirtileri ${d.taban_suyu_belirti.toLowerCase()} durumundadır.`;
        if (d.taskin_olasiligi) {
          const hasTaskin = d.taskin_olasiligi.toLowerCase().includes("yok")
            ? "yoktur"
            : "vardır";
          combinedNotes += ` Taşkın olasılığı ${hasTaskin}.`;
        }
        // Notlarda yalnizca ilk harf buyuk, gerisi kucuk yazilir.
        combinedNotes = basHarfBuyut(kucult(combinedNotes));

        const alanTablolariHtml = alanSatirlari
          .map((satir) => generateTableHtml(satir))
          .join("");

        htmlParts.push(`
          <div class="Section1">
            <table style="width: 100%; border: 2px solid #000; margin-bottom: 10px; border-collapse: collapse;">
              <tr>
                <td style="padding: 6px; background-color: #f8f8f8; width: 100%; vertical-align: top;">
                  <div style="font-size:12pt; font-weight:bold; margin-bottom:3px; color:#333;">PROFİL NO: ${profilNoYazi(profile)}</div>
                  <div style="font-size:8pt; color:#555;"><b>Koordinat:</b> X: ${d.koordinat_x || "-"} | Y: ${d.koordinat_y || "-"} | Z: ${d.yukseklik || "-"} m</div>
                  <div style="font-size:8pt; color:#555;"><b>Konum:</b> ${profile.il} / ${profile.ilce} / ${profile.mahalle}</div>
                </td>
              </tr>
            </table>

            <table style="width: 100%; border: none; table-layout: fixed; margin-bottom: 0;">
              <tr>
                ${
                  horizonImgHtmlModified
                    ? `<td style="width: 35%; border: none; padding-right: 10px; vertical-align: top;">${horizonImgHtmlModified}</td>`
                    : ""
                }
                <td style="width: ${horizonImgHtmlModified ? "65%" : "100%"}; border: none; vertical-align: top; padding: 0;">
                  <table class="horizon-table" style="width: 100%; border-collapse: collapse; table-layout: fixed; border: 1px solid #000;">
                    <thead>
                      <tr>
                        <th class="col-hor" style="border: 1px solid #000; background-color: #e0e0e0; font-weight: bold; text-align: center; font-size: 8pt; padding: 4px;">HORİZON</th>
                        <th class="col-dep" style="border: 1px solid #000; background-color: #e0e0e0; font-weight: bold; text-align: center; font-size: 8pt; padding: 4px;">CM</th>
                        <th class="col-des" style="border: 1px solid #000; background-color: #e0e0e0; font-weight: bold; text-align: center; font-size: 8pt; padding: 4px;">AÇIKLAMA</th>
                        <th class="col-ozel" style="border: 1px solid #000; background-color: #e0e0e0; font-weight: bold; text-align: center; font-size: 8pt; padding: 4px;">ÖZEL GÖRÜNÜM</th>
                      </tr>
                    </thead>
                    <tbody>${horizonRows}</tbody>
                  </table>
                </td>
              </tr>
            </table>
          </div>
          ${pdfMi ? "" : `<br clear=all style='mso-break-type:section-break'>`}
          <div class="Section2">
            ${pdfMi ? `<div class="yatayCerceve"><div class="yatayIc">` : ""}
            <div style="border-bottom: 2px solid #000; padding-bottom:4px; margin-bottom:10px; font-weight:bold; font-size:11pt; color: #333;">
               PROFİL NO ${profilNoYazi(profile)} ÇEVRESİ
            </div>
            ${alanTablolariHtml}
            <table style="margin-top:8px; width: 100%; border-collapse: collapse; table-layout: fixed; border: 1px solid #000;">
               <tr>
                 <td style="width: 10%; border-right: 1px solid #000; font-weight:bold; background-color:#e0e0e0; vertical-align: middle; text-align: center; font-size: 8pt; padding: 4px;">NOTLAR</td>
                 <td style="width: 90%; text-align:left; font-size:8.5pt; padding: 4px; line-height: 1.2;">
                   ${combinedNotes}
                 </td>
               </tr>
            </table>
            ${envImagesHtml}
            ${pdfMi ? `</div></div>` : ""}
          </div>
        `);

        if (!pdfMi && index < profilesToExport.length - 1) {
          htmlParts.push(
            `<br clear=all style='page-break-before:always; mso-break-type:section-break'>`,
          );
        }
      }

      // PDF'te yatik cerceve 180mm yuksekliginde; icerik daha uzunsa
      // kirpilmasin diye oranli kucultuluyor. (Word ciktisina dokunmaz.)
      const yatayUyarlamaBetigi = `
        <script>
        (function () {
          var HEDEF_PX = 180 * 96 / 25.4; // 180mm -> CSS px
          var kutular = document.querySelectorAll(".yatayIc");
          for (var i = 0; i < kutular.length; i++) {
            var el = kutular[i];
            var yukseklik = el.scrollHeight;
            if (yukseklik > HEDEF_PX) {
              el.style.transform = "scale(" + HEDEF_PX / yukseklik + ")";
            }
          }
        })();
        \x3C/script>`;

      htmlParts.push(`
        ${pdfMi ? yatayUyarlamaBetigi : ""}
        </body>
        </html>
      `);

      setProgress(97);
      setProgressText(
        pdfMi ? "Yazdırma önizlemesi hazırlanıyor..." : "Belge derleniyor...",
      );

      if (pdfMi) {
        // PDF: icerik ayni kalir, yalnizca kagit yatay cevrilir.
        // Once sunucuda (puppeteer) gercek bir .pdf dosyasi uretilir.
        const pdfHtml = htmlParts.join("");
        try {
          setProgressText("PDF sunucuda oluşturuluyor...");
          const res = await api.post(
            "/admin/morfoloji-pdf",
            { html: pdfHtml, jobName },
            { responseType: "blob", timeout: 300000 },
          );
          const pdfUrl = URL.createObjectURL(
            new Blob([res.data], { type: "application/pdf" }),
          );
          const pdfLink = document.createElement("a");
          pdfLink.href = pdfUrl;
          pdfLink.download = `Toplu_Morfoloji_${jobName
            .replace(/[^a-z0-9]/gi, "_")
            .toLowerCase()}.pdf`;
          document.body.appendChild(pdfLink);
          pdfLink.click();
          setTimeout(() => {
            document.body.removeChild(pdfLink);
            URL.revokeObjectURL(pdfUrl);
          }, 100);
          setProgress(100);
          setProgressText("PDF indirildi.");
        } catch (pdfHata) {
          // Sunucu PDF uretemezse belge kaybolmasin: yazdirma penceresi acilir,
          // kullanici "PDF olarak kaydet" ile ayni ciktiyi alir.
          console.error("Sunucu PDF hatasi, yazdırmaya düşülüyor:", pdfHata);
          setProgressText("Yazdırma penceresi açılıyor...");
          await yazdirmaPenceresiAc(pdfHtml);
          setProgress(100);
          alert(
            'Sunucuda PDF üretilemedi. Yazdırma penceresi açıldı: "Hedef" olarak "PDF olarak kaydet" seçerek aynı çıktıyı alabilirsiniz.',
          );
        }
        setOpen(false);
        return;
      }

      const blobArray = ["\ufeff"];
      for (let i = 0; i < htmlParts.length; i++) {
        blobArray.push(htmlParts[i]);
      }

      const blob = new Blob(blobArray, {
        type: "application/msword;charset=utf-8",
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");

      link.href = url;
      const safeJobName = jobName.replace(/[^a-z0-9]/gi, "_").toLowerCase();
      link.download = `Toplu_Morfoloji_${safeJobName}.doc`;

      document.body.appendChild(link);
      link.click();

      setTimeout(() => {
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      }, 100);

      setProgress(100);
      setProgressText("Tamamlandı!");
      setOpen(false);
    } catch (error) {
      console.error(error);
      alert(
        "Toplu morfoloji oluşturulurken hata oluştu. Lütfen konsolu kontrol edin.",
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Tooltip title="Bu çalışmadaki TÜM veya SEÇİLİ profillerin morfoloji belgelerini tek bir Word dosyasında indirir">
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <IconButton
            onClick={() => setOpen(true)}
            sx={{
              color: "#5D4037",
              bgcolor: "rgba(93, 64, 55, 0.05)",
              mb: 0.5,
              "&:hover": { bgcolor: "rgba(93, 64, 55, 0.15)" },
            }}
          >
            <Description fontSize="small" />
          </IconButton>
          <Typography
            variant="caption"
            sx={{
              fontSize: "0.6rem",
              color: "text.secondary",
              fontWeight: "bold",
            }}
          >
            MORFOLOJİ
          </Typography>
        </Box>
      </Tooltip>

      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ bgcolor: "#f5f5f5" }}>
          <Box display="flex" alignItems="center" gap={1}>
            <Settings color="action" /> Toplu Morfoloji Oluştur
          </Box>
          <Stepper activeStep={activeStep} sx={{ mt: 2 }}>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
        </DialogTitle>

        <DialogContent
          dividers
          sx={{ p: activeStep === 0 ? 0 : 2, minHeight: "400px" }}
        >
          {fetchingProfiles ? (
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              height="100%"
              p={4}
            >
              <CircularProgress />
            </Box>
          ) : (
            <>
              {activeStep === 0 && (
                <Box>
                  <Box p={2} bgcolor="#fafafa" borderBottom="1px solid #e0e0e0">
                    <Grid container spacing={2}>
                      <Grid item xs={4}>
                        <FormControl fullWidth size="small">
                          <InputLabel>İl Seçin</InputLabel>
                          <Select
                            value={filterIl}
                            label="İl Seçin"
                            onChange={handleIlChange}
                          >
                            <MenuItem value="ALL">-- Tüm İller --</MenuItem>
                            {uniqueIls.map((loc, idx) => (
                              <MenuItem key={idx} value={loc}>
                                {loc}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={4}>
                        <FormControl
                          fullWidth
                          size="small"
                          disabled={filterIl === "ALL"}
                        >
                          <InputLabel>İlçe Seçin</InputLabel>
                          <Select
                            value={filterIlce}
                            label="İlçe Seçin"
                            onChange={handleIlceChange}
                          >
                            <MenuItem value="ALL">-- Tüm İlçeler --</MenuItem>
                            {uniqueIlces.map((loc, idx) => (
                              <MenuItem key={idx} value={loc}>
                                {loc}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={4}>
                        <FormControl
                          fullWidth
                          size="small"
                          disabled={filterIlce === "ALL"}
                        >
                          <InputLabel>Mahalle Seçin</InputLabel>
                          <Select
                            value={filterMahalle}
                            label="Mahalle Seçin"
                            onChange={handleMahalleChange}
                          >
                            <MenuItem value="ALL">
                              -- Tüm Mahalleler --
                            </MenuItem>
                            {uniqueMahalles.map((loc, idx) => (
                              <MenuItem key={idx} value={loc}>
                                {loc}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>
                    </Grid>

                    <Box
                      display="flex"
                      justifyContent="space-between"
                      alignItems="center"
                      mt={2}
                    >
                      <Typography variant="body2" color="text.secondary">
                        Listelenen: <b>{filteredProfiles.length}</b> | Seçili:{" "}
                        <b>{selectedProfileIds.length}</b>
                      </Typography>
                      <Box display="flex" gap={1}>
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<CheckBox />}
                          onClick={handleSelectAllFiltered}
                        >
                          Listedekileri Seç
                        </Button>
                        <Button
                          size="small"
                          variant="outlined"
                          color="error"
                          startIcon={<CheckBoxOutlineBlank />}
                          onClick={handleDeselectAllFiltered}
                        >
                          Listedekileri Kaldır
                        </Button>
                      </Box>
                    </Box>
                  </Box>

                  <List sx={{ maxHeight: 350, overflow: "auto", p: 0 }}>
                    {filteredProfiles.length === 0 && (
                      <Box p={3} textAlign="center" color="text.secondary">
                        Bu konuma ait profil bulunamadı.
                      </Box>
                    )}
                    {filteredProfiles.map((p) => {
                      const isChecked = selectedProfileIds.includes(p.id);
                      return (
                        <React.Fragment key={p.id}>
                          <ListItem
                            button
                            onClick={() => handleProfileToggle(p.id)}
                            sx={{
                              bgcolor: isChecked
                                ? "rgba(25, 118, 210, 0.08)"
                                : "inherit",
                            }}
                          >
                            <Checkbox
                              checked={isChecked}
                              tabIndex={-1}
                              disableRipple
                              color="primary"
                            />
                            <ListItemText
                              primary={<b>Profil: {p.profil_no}</b>}
                              secondary={
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  display="flex"
                                  alignItems="center"
                                  gap={0.5}
                                >
                                  <Map fontSize="inherit" /> {p.il} / {p.ilce} /{" "}
                                  {p.mahalle}
                                </Typography>
                              }
                            />
                          </ListItem>
                          <Divider />
                        </React.Fragment>
                      );
                    })}
                  </List>
                </Box>
              )}

              {activeStep === 1 && (
                <Box>
                  <Alert severity="info" sx={{ mb: 2 }}>
                    Seçili <b>{selectedProfileIds.length}</b> profil için çevre
                    bölümünde görünecek alanları seçin (En fazla {MAX_ALAN}{" "}
                    adet). Alanlar 2. sayfada <b>4 satır</b> halinde dizilir.
                  </Alert>
                  <Grid container spacing={1}>
                    {availableOptions.map((opt) => (
                      <Grid item xs={4} key={opt.key}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={selectedFields.includes(opt.key)}
                              onChange={() => handleFieldToggle(opt.key)}
                              color="primary"
                              disabled={
                                !selectedFields.includes(opt.key) &&
                                selectedFields.length >= MAX_ALAN
                              }
                            />
                          }
                          label={
                            <Typography variant="body2">{opt.label}</Typography>
                          }
                        />
                      </Grid>
                    ))}
                  </Grid>
                  <Typography
                    variant="caption"
                    sx={{ display: "block", mt: 2, textAlign: "right" }}
                  >
                    Seçilen: {selectedFields.length} / {MAX_ALAN} (
                    {MAX_ALAN - selectedFields.length} hak kaldı)
                  </Typography>

                  <Divider sx={{ my: 2 }}>RESİMLER</Divider>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: "block", mb: 1 }}
                  >
                    Resimler belgeye gömüldüğü için dosyayı büyütür ve oluşturma
                    süresini uzatır. İstemiyorsanız kapatabilirsiniz.
                  </Typography>
                  <Stack direction="row" spacing={3} flexWrap="wrap">
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={includeHorizonImage}
                          onChange={(e) =>
                            setIncludeHorizonImage(e.target.checked)
                          }
                          color="primary"
                        />
                      }
                      label={
                        <Typography variant="body2">
                          Horizon (profil kesiti) resmi alınsın
                        </Typography>
                      }
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={includeEnvImages}
                          onChange={(e) =>
                            setIncludeEnvImages(e.target.checked)
                          }
                          color="primary"
                        />
                      }
                      label={
                        <Typography variant="body2">
                          Çevre fotoğrafları alınsın
                        </Typography>
                      }
                    />
                  </Stack>
                </Box>
              )}

              {activeStep === 2 && (
                <Box>
                  <Alert severity="info" sx={{ mb: 2 }}>
                    Belgenin hangi biçimde üretileceğini seçin.
                  </Alert>

                  <Stack spacing={1.5}>
                    <Box
                      onClick={() => !loading && setCiktiBicimi("word")}
                      sx={{
                        border: "2px solid",
                        borderColor:
                          ciktiBicimi === "word" ? "primary.main" : "#e0e0e0",
                        bgcolor:
                          ciktiBicimi === "word"
                            ? "rgba(25, 118, 210, 0.06)"
                            : "transparent",
                        borderRadius: 1,
                        p: 2,
                        cursor: loading ? "default" : "pointer",
                      }}
                    >
                      <FormControlLabel
                        control={
                          <Radio
                            checked={ciktiBicimi === "word"}
                            onChange={() => setCiktiBicimi("word")}
                            disabled={loading}
                          />
                        }
                        label={
                          <Box>
                            <Typography variant="body2" fontWeight="bold">
                              WORD (.doc) — düzenlenebilir
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              Mevcut düzenin aynısı: 1. sayfa dikey, 2. sayfa
                              yatay. Word'de açılıp üzerinde değişiklik
                              yapılabilir.
                            </Typography>
                          </Box>
                        }
                        sx={{ m: 0, alignItems: "flex-start" }}
                      />
                    </Box>

                    <Box
                      onClick={() => !loading && setCiktiBicimi("pdf")}
                      sx={{
                        border: "2px solid",
                        borderColor:
                          ciktiBicimi === "pdf" ? "primary.main" : "#e0e0e0",
                        bgcolor:
                          ciktiBicimi === "pdf"
                            ? "rgba(25, 118, 210, 0.06)"
                            : "transparent",
                        borderRadius: 1,
                        p: 2,
                        cursor: loading ? "default" : "pointer",
                      }}
                    >
                      <FormControlLabel
                        control={
                          <Radio
                            checked={ciktiBicimi === "pdf"}
                            onChange={() => setCiktiBicimi("pdf")}
                            disabled={loading}
                          />
                        }
                        label={
                          <Box>
                            <Typography variant="body2" fontWeight="bold">
                              PDF — doğrudan yazdırmaya hazır
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              Kâğıt <b>dikey</b>; 1. sayfa Word'ün birebir
                              aynısı. 2. sayfanın içeriği hiç değişmeden{" "}
                              <b>yan yatık</b> basılır, yani sayfa çevrilerek
                              okunur. Doğrudan yazdırılmaya hazır .pdf iner.
                            </Typography>
                          </Box>
                        }
                        sx={{ m: 0, alignItems: "flex-start" }}
                      />
                    </Box>
                  </Stack>

                  {loading && (
                    <Box sx={{ mt: 3 }}>
                      <Stack
                        direction="row"
                        justifyContent="space-between"
                        sx={{ mb: 0.5 }}
                      >
                        <Typography variant="caption" color="text.secondary">
                          {progressText}
                        </Typography>
                        <Typography variant="caption" fontWeight="bold">
                          %{progress}
                        </Typography>
                      </Stack>
                      <LinearProgress
                        variant="determinate"
                        value={progress}
                        sx={{ height: 8, borderRadius: 4 }}
                      />
                    </Box>
                  )}
                </Box>
              )}
            </>
          )}
        </DialogContent>

        <DialogActions>
          <Button
            onClick={() => setOpen(false)}
            color="inherit"
            disabled={loading}
          >
            İptal
          </Button>
          {activeStep === 0 && (
            <Button
              onClick={() => setActiveStep(1)}
              variant="contained"
              disabled={fetchingProfiles || selectedProfileIds.length === 0}
            >
              İleri
            </Button>
          )}
          {activeStep === 1 && (
            <>
              <Button onClick={() => setActiveStep(0)}>Geri</Button>
              <Button
                onClick={() => setActiveStep(2)}
                variant="contained"
                disabled={selectedFields.length === 0}
              >
                İleri
              </Button>
            </>
          )}
          {activeStep === 2 && (
            <>
              <Button onClick={() => setActiveStep(1)} disabled={loading}>
                Geri
              </Button>
              <Button
                onClick={handleCreateMorphology}
                variant="contained"
                color="primary"
                disabled={loading}
                startIcon={
                  loading ? (
                    <CircularProgress size={20} color="inherit" />
                  ) : null
                }
              >
                {loading
                  ? `Oluşturuluyor... %${progress}`
                  : ciktiBicimi === "pdf"
                    ? "PDF Olarak Çıkar"
                    : "Word Olarak İndir"}
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>
    </>
  );
}
